import { useQuery } from '@tanstack/react-query';
import { GitBranch } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { getCrossProjectIntelligence } from '@/lib/api';

/**
 * PhaseDistributionPanel
 *
 * Groups projects by lifecycle phase; sorted phase asc, Unphased last
 */
export function PhaseDistributionPanel(): React.ReactElement | null {
  const { data, isLoading } = useQuery({
    queryKey: ['intelligence'],
    queryFn: getCrossProjectIntelligence,
    staleTime: 30_000,
    refetchInterval: 30_000,
  });

  if (isLoading) {
    return <div className="text-xs text-text-secondary">Loading phases...</div>;
  }

  if (!data) {
    return null;
  }

  // Group projects by phase
  const phaseMap = new Map<number | null, string[]>();
  for (const project of data.projects) {
    const phaseNum = project.lifecyclePhase?.current ?? null;
    if (!phaseMap.has(phaseNum)) {
      phaseMap.set(phaseNum, []);
    }
    const projects = phaseMap.get(phaseNum);
    if (projects) {
      projects.push(project.name);
    }
  }

  // Sort: phases 1-10 ascending, then null (Unphased)
  const sorted = Array.from(phaseMap.entries()).sort((a, b) => {
    const aNum = a[0] ?? Infinity;
    const bNum = b[0] ?? Infinity;
    return aNum - bNum;
  });

  return (
    <div className="flex flex-1 flex-col gap-2">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-text-tertiary flex items-center gap-1">
        <GitBranch className="h-3.5 w-3.5" />
        Phase Distribution
      </div>

      {sorted.length > 0 ? (
        <div className="flex-1 space-y-2 overflow-y-auto">
          {sorted.map(([phase, projects]) => (
            <div key={phase ?? 'unphased'} className="space-y-0.5">
              <div className="flex items-center gap-1">
                <Badge variant="secondary" className="text-[9px]">
                  {phase === null ? 'Unphased' : `Phase ${phase}`}
                </Badge>
                <span className="text-[9px] text-text-tertiary">({projects.length})</span>
              </div>
              <div className="text-[9px] text-text-secondary space-y-0.5">
                {projects.slice(0, 3).map((name, i) => (
                  <div key={i} className="truncate ml-2">
                    • {name}
                  </div>
                ))}
                {projects.length > 3 && (
                  <div className="text-[8px] text-text-tertiary ml-2">
                    + {projects.length - 3} more
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="rounded bg-surface-elevated/50 p-2 text-center text-[10px] text-text-tertiary">
          No phase data
        </div>
      )}
    </div>
  );
}
